/**
 * WgPeerFormDialog (rc148) — crear o editar un peer WireGuard y vincularlo a
 * un router. La IP del túnel y las llaves las asigna ByteISP.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Waypoints } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { useListRouters } from "@/features/routers/api";
import type { ApiError } from "@/lib/api";

import { useCrearWgPeer, useEditarWgPeer } from "./wgApi";
import type { WgAplicacion, WgPeer } from "./wgSchemas";

type Props = {
  peer: WgPeer | null; // null = crear
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated?: (peer: WgPeer) => void;
};

export function avisarAplicacion(a: WgAplicacion, okTitulo: string) {
  if (a.aplicado) toast.success(okTitulo, { description: a.mensaje });
  else toast.warning(`${okTitulo} — pendiente de aplicar`, { description: a.mensaje });
}

export function WgPeerFormDialog({ peer, open, onOpenChange, onCreated }: Props) {
  const isEdit = peer !== null;
  const [descripcion, setDescripcion] = useState("");
  const [routerId, setRouterId] = useState("");
  const [activo, setActivo] = useState(true);

  const { data: routersData } = useListRouters({ size: 200 });
  const routerOptions: ComboboxOption[] = (routersData?.items ?? []).map((r) => ({
    value: r.id, label: r.nombre, hint: r.ip_address,
  }));

  const crearMut = useCrearWgPeer();
  const editarMut = useEditarWgPeer(peer?.id ?? "");

  useEffect(() => {
    if (!open) return;
    setDescripcion(peer?.descripcion ?? "");
    setRouterId(peer?.router_id ?? "");
    setActivo(peer?.activo ?? true);
  }, [open, peer]);

  const usaWg = !!peer?.router_usa_wg;

  async function handleSubmit() {
    if (!descripcion.trim()) {
      toast.error("Descripción requerida", { description: "Ej. \"Torre Cerro Verde\"." });
      return;
    }
    try {
      if (isEdit) {
        const res = await editarMut.mutateAsync({
          descripcion: descripcion.trim(),
          router_id: routerId || null,
          activo,
        });
        avisarAplicacion(res.aplicacion, "Peer actualizado");
        onOpenChange(false);
      } else {
        const res = await crearMut.mutateAsync({
          descripcion: descripcion.trim(),
          router_id: routerId || null,
        });
        avisarAplicacion(res.aplicacion, `Peer creado — ${res.peer.ip_tunel}`);
        onCreated?.(res.peer);
      }
    } catch (e) {
      toast.error(isEdit ? "No se pudo actualizar" : "No se pudo crear", {
        description: (e as ApiError).message,
      });
    }
  }

  const isPending = crearMut.isPending || editarMut.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Waypoints size={15} />
          {isEdit ? "Editar peer WireGuard" : "Nuevo peer WireGuard"}
        </DialogTitle>
        {!isEdit && (
          <p className="text-xs text-muted-foreground">
            ByteISP genera las llaves y asigna la siguiente IP libre del túnel.
            Después te muestra el script para pegar en el MikroTik.
          </p>
        )}
      </DialogHeader>
      <DialogContent>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Descripción *</Label>
            <Input
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Ej. Torre Cerro Verde"
              autoFocus
            />
          </div>

          <div className="flex flex-col gap-1">
            <Label className="text-xs">Vincular a router (opcional)</Label>
            <Combobox
              options={routerOptions}
              value={routerId}
              onChange={setRouterId}
              placeholder="— Sin asignar —"
              allowClear
              disabled={usaWg}
            />
            <p className="text-[10px] text-muted-foreground">
              {usaWg
                ? "El router ya usa este túnel. Para cambiarlo, primero regrésalo a OpenVPN."
                : "Vincúlalo para poder pasar ese router a WireGuard desde su ficha."}
            </p>
          </div>

          {isEdit && (
            <label className="flex cursor-pointer items-start gap-2 text-xs">
              <input
                type="checkbox"
                checked={activo}
                onChange={(e) => setActivo(e.target.checked)}
                disabled={usaWg}
                className="mt-0.5"
              />
              <div>
                <span className="font-medium">Peer activo</span>
                <div className="text-[10px] text-muted-foreground">
                  Inactivo = se quita del servidor (el router no conecta) pero se
                  conservan su IP y sus llaves.
                </div>
              </div>
            </label>
          )}

          {isEdit && peer && (
            <div className="rounded-md border bg-muted/20 px-2.5 py-2 text-[11px]">
              <div><span className="text-muted-foreground">IP del túnel:</span>{" "}
                <span className="font-mono">{peer.ip_tunel}</span></div>
              <div className="truncate"><span className="text-muted-foreground">Llave pública:</span>{" "}
                <span className="font-mono">{peer.public_key}</span></div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={isPending}>
              Cancelar
            </Button>
            <Button size="sm" onClick={handleSubmit} disabled={isPending}>
              {isPending ? "Guardando…" : isEdit ? "Guardar" : "Crear peer"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
